import axios, {AxiosResponse} from "axios";

interface IRunningCall {
  url: string;
  call: Promise<AxiosResponse>;
}

export interface IBackend {
  getList<T>(url: string): Promise<T[] | undefined>;
  get<T>(url: string): Promise<T | undefined>;
  save<T>(url: string, data: T): Promise<boolean>;
}

export class Backend implements IBackend {
  private readonly _baseUrl: string;
  private _activeCalls: IRunningCall[];

  constructor(baseUrl: string) {
    axios.defaults.headers.get["Access-Control-Allow-Origin"] = "*";
    axios.defaults.headers.get["Access-Control-Allow-Credentials"] = "true";
    this._baseUrl = baseUrl;
    this._activeCalls = [];
  }

  private getCall(url: string): IRunningCall | undefined {
    const call = this._activeCalls.filter(call => call.url === url);
    if(call.length){
      return call[0];
    }
    return undefined;
  }

  private makeGetRequest<T>(url: string): Promise<AxiosResponse<T>> {
    const existingCall = this.getCall(url);
    if (existingCall) {
      return existingCall.call;
    }

    const newCall = axios.get<T>(`${this._baseUrl}/${url}`);

    this._activeCalls.push({
      url,
      call: newCall
    });

    newCall.then(() => {
      this._activeCalls = this._activeCalls.filter(call => call.url !== url);
    });

    return newCall;
  }

  private async makePostRequest<T>(url: string, data: T): Promise<boolean> {
    const result = await axios.post(url, data);
    return result.data;
  }

  public async getList<T>(url: string): Promise<T[] | undefined> {
    const data = await this.makeGetRequest<T[]>(url);
    if (data) {
      return data.data;
    }
    return undefined;
  }

  public async get<T>(url:string): Promise<T | undefined> {
    const data = await this.makeGetRequest<T>(url);
    if (data) {
      return data.data;
    }
    return undefined;
  }

  public async save<T>(url: string, data: T): Promise<boolean> {
    const success = await this.makePostRequest<T>(url, data);
    return success;
  }
}